import React, { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Mail, Send, Eye, Power, PowerOff, CheckCircle, XCircle,
  AlertTriangle, Clock, UserPlus, FileText, Briefcase, Bell, Gift, BarChart3, Megaphone, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import CustomBroadcastsSection from './CustomBroadcastsSection';

const TEMPLATE_ICONS = {
  welcome: Gift,
  password_reset: AlertTriangle,
  application_received: FileText,
  application_status: Clock,
  team_invite: UserPlus,
  job_alert: Bell,
  employer_onboarding: Briefcase,
  subscription_reminder: AlertTriangle,
  weekly_digest: BarChart3,
  launch_announcement: Megaphone,
};

const EmailTemplatesTab = ({ token }) => {
  const [masterEnabled, setMasterEnabled] = useState(false);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewKey, setPreviewKey] = useState('');
  const [testSending, setTestSending] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastKey, setBroadcastKey] = useState('');
  const [broadcastStatus, setBroadcastStatus] = useState(null);
  const [broadcastStarting, setBroadcastStarting] = useState(false);
  const [broadcastOnlyNew, setBroadcastOnlyNew] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-templates`, { headers, withCredentials: true });
      setMasterEnabled(res.data.email_master_enabled);
      setTemplates(res.data.templates);
    } catch { toast.error('Failed to load email settings'); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        email_master_enabled: masterEnabled,
        templates: Object.fromEntries(
          Object.entries(templates).map(([k, v]) => [k, { enabled: v.enabled, subject: v.subject }])
        ),
      };
      const res = await axios.put(`${API}/admin/email-templates`, payload, { headers, withCredentials: true });
      setMasterEnabled(res.data.email_master_enabled);
      setTemplates(res.data.templates);
      toast.success('Email settings saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const toggleTemplate = (key) => {
    setTemplates(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled },
    }));
  };

  const updateSubject = (key, subject) => {
    setTemplates(prev => ({
      ...prev,
      [key]: { ...prev[key], subject },
    }));
  };

  const handlePreview = async (key) => {
    try {
      const res = await axios.get(`${API}/admin/email-templates/preview/${key}`, { headers, withCredentials: true });
      setPreviewHtml(res.data.html);
      setPreviewKey(key);
      setPreviewOpen(true);
    } catch { toast.error('Failed to load preview'); }
  };

  const handleTestSend = async (key) => {
    setTestSending(key);
    try {
      const res = await axios.post(`${API}/admin/email-templates/test`, { template_key: key }, { headers, withCredentials: true });
      if (res.data.result?.sent) {
        toast.success(`Test email sent for "${templates[key]?.label}"`);
      } else {
        toast.info(`Email not sent: ${res.data.result?.reason?.replace(/_/g, ' ')}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    }
    setTestSending(null);
  };

  const openBroadcast = async (key) => {
    setBroadcastKey(key);
    setBroadcastOpen(true);
    setBroadcastStatus(null);
    try {
      const res = await axios.get(`${API}/admin/email-broadcast/${key}/status`, { headers, withCredentials: true });
      setBroadcastStatus(res.data);
    } catch {
      toast.error('Failed to load broadcast status');
    }
  };

  const refreshBroadcastStatus = async (key = broadcastKey) => {
    if (!key) return;
    try {
      const res = await axios.get(`${API}/admin/email-broadcast/${key}/status`, { headers, withCredentials: true });
      setBroadcastStatus(res.data);
    } catch { /* silent */ }
  };

  const startBroadcast = async () => {
    if (!broadcastKey) return;
    setBroadcastStarting(true);
    try {
      const res = await axios.post(
        `${API}/admin/email-broadcast/${broadcastKey}/send`,
        { only_never_sent: broadcastOnlyNew },
        { headers, withCredentials: true }
      );
      toast.success(res.data.message || 'Broadcast started');
      // Poll status every 3s while dialog open
      setTimeout(() => refreshBroadcastStatus(broadcastKey), 1500);
      setTimeout(() => refreshBroadcastStatus(broadcastKey), 5000);
      setTimeout(() => refreshBroadcastStatus(broadcastKey), 15000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start broadcast');
    }
    setBroadcastStarting(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const enabledCount = Object.values(templates).filter(t => t.enabled).length;
  const totalCount = Object.keys(templates).length;

  return (
    <div className="space-y-6" data-testid="email-templates-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Email Templates</h2>
          <p className="text-sm text-slate-500 mt-1">Manage transactional emails sent by EdAssist</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="email-save-btn">
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      {/* Master Switch */}
      <Card className={masterEnabled ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'} data-testid="email-master-switch-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {masterEnabled ? (
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Power className="w-5 h-5 text-green-600" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <PowerOff className="w-5 h-5 text-red-500" />
                </div>
              )}
              <div>
                <p className="font-semibold text-slate-900">
                  {masterEnabled ? 'Email Sending is ON' : 'Email Sending is OFF'}
                </p>
                <p className="text-sm text-slate-500">
                  {masterEnabled
                    ? `${enabledCount} of ${totalCount} templates active. Emails will be sent via ZeptoMail.`
                    : 'All transactional emails are paused. Toggle on to start sending.'}
                </p>
              </div>
            </div>
            <Switch
              checked={masterEnabled}
              onCheckedChange={setMasterEnabled}
              data-testid="email-master-switch"
            />
          </div>
        </CardContent>
      </Card>

      {!masterEnabled && (
        <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Master switch is off — no emails will be sent regardless of individual template settings.
        </div>
      )}

      {/* Template Cards */}
      <div className="space-y-3">
        {Object.entries(templates).map(([key, tpl]) => {
          const Icon = TEMPLATE_ICONS[key] || Mail;
          return (
            <Card key={key} className={`transition-colors ${!tpl.enabled ? 'opacity-60' : ''}`} data-testid={`email-tpl-${key}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${tpl.enabled ? 'bg-indigo-50' : 'bg-slate-100'}`}>
                    <Icon className={`w-4 h-4 ${tpl.enabled ? 'text-indigo-600' : 'text-slate-400'}`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-slate-900 text-sm">{tpl.label}</p>
                      <Badge className={tpl.enabled ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-slate-100 text-slate-500 text-[10px]'}>
                        {tpl.enabled ? 'Active' : 'Disabled'}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">{tpl.description}</p>
                    <div className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Clock className="w-3 h-3" />
                      <span>Trigger: {tpl.trigger}</span>
                    </div>

                    {/* Subject Line */}
                    <div className="mt-3">
                      <Label className="text-xs text-slate-500">Subject Line</Label>
                      <Input
                        value={tpl.subject || ''}
                        onChange={e => updateSubject(key, e.target.value)}
                        className="mt-1 h-8 text-sm"
                        data-testid={`email-subject-${key}`}
                      />
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePreview(key)}
                      className="h-8 px-2"
                      data-testid={`email-preview-${key}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTestSend(key)}
                      disabled={testSending === key}
                      className="h-8 px-2"
                      data-testid={`email-test-${key}`}
                    >
                      <Send className="w-3.5 h-3.5 mr-1" />
                      {testSending === key ? 'Sending...' : 'Test'}
                    </Button>
                    {tpl.is_broadcast && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openBroadcast(key)}
                        className="h-8 px-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                        data-testid={`email-broadcast-${key}`}
                      >
                        <Megaphone className="w-3.5 h-3.5 mr-1" /> Broadcast
                      </Button>
                    )}
                    <Switch
                      checked={tpl.enabled}
                      onCheckedChange={() => toggleTemplate(key)}
                      data-testid={`email-toggle-${key}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Custom Broadcasts */}
      <div className="pt-6 mt-6 border-t border-slate-200">
        <CustomBroadcastsSection token={token} masterEnabled={masterEnabled} />
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>
              Preview: {templates[previewKey]?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto border rounded-lg bg-slate-100 p-2" style={{ maxHeight: 'calc(85vh - 100px)' }}>
            <iframe
              srcDoc={previewHtml}
              title="Email Preview"
              className="w-full bg-white rounded"
              style={{ height: '600px', border: 'none' }}
              data-testid="email-preview-iframe"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent className="max-w-lg" data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Megaphone className="w-5 h-5 text-indigo-600" />
              Broadcast: {templates[broadcastKey]?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-slate-600 leading-relaxed">
              This is a <strong>one-time announcement</strong> to all existing users. Emails go out only if the master switch and this template are ON, and ZeptoMail is configured.
            </div>

            {broadcastStatus ? (
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-indigo-700">{broadcastStatus.total_recipients}</p>
                  <p className="text-[10px] uppercase tracking-wide text-indigo-600 mt-1">Eligible Users</p>
                </div>
                <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{broadcastStatus.already_sent}</p>
                  <p className="text-[10px] uppercase tracking-wide text-green-600 mt-1">Already Sent</p>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{Math.max(0, broadcastStatus.total_recipients - broadcastStatus.already_sent)}</p>
                  <p className="text-[10px] uppercase tracking-wide text-amber-600 mt-1">Remaining</p>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500">Loading recipient count...</div>
            )}

            {broadcastStatus?.last_run && (
              <div className={`rounded-lg border p-3 text-xs ${broadcastStatus.last_run.status === 'running' ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="font-semibold">
                    Last run: {broadcastStatus.last_run.status}
                  </span>
                </div>
                <div>
                  Sent: <strong>{broadcastStatus.last_run.sent}</strong> &nbsp;·&nbsp;
                  Skipped: <strong>{broadcastStatus.last_run.skipped}</strong> &nbsp;·&nbsp;
                  Failed: <strong>{broadcastStatus.last_run.failed}</strong> &nbsp;·&nbsp;
                  Total: <strong>{broadcastStatus.last_run.total}</strong>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <Switch checked={broadcastOnlyNew} onCheckedChange={setBroadcastOnlyNew} data-testid="broadcast-only-new" />
              <div>
                <p className="text-sm font-medium text-slate-800">Only send to users who haven't received this yet</p>
                <p className="text-xs text-slate-500 mt-0.5">Recommended. Prevents duplicate sends if you run this again.</p>
              </div>
            </div>

            {!masterEnabled && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                Master email switch is OFF. Emails will be logged but not delivered until you turn it on.
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => refreshBroadcastStatus()} data-testid="broadcast-refresh">
                Refresh
              </Button>
              <Button variant="outline" onClick={() => setBroadcastOpen(false)}>Cancel</Button>
              <Button
                onClick={startBroadcast}
                disabled={broadcastStarting || broadcastStatus?.last_run?.status === 'running'}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="broadcast-send-btn"
              >
                <Send className="w-4 h-4 mr-1.5" />
                {broadcastStarting
                  ? 'Starting...'
                  : broadcastStatus?.last_run?.status === 'running'
                    ? 'Running...'
                    : `Send to ${broadcastStatus ? Math.max(0, broadcastStatus.total_recipients - (broadcastOnlyNew ? broadcastStatus.already_sent : 0)) : '...'} users`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesTab;
